<template>
  <div class="page-flags">

    <!-- Background -->
    <ui-wad class="overflow-hidden">
      <div class="page-flags__background" />
    </ui-wad>

    <!-- Content -->
    <div class="page-flags__content">

      <ui-button-icon
        class="page-flags__previous"
        name="arrow_back"
        :variant="UI.VARIANT.DARK"
        focus-ring="active"
        focus-ring-variant="dark"
        @click="() => goToPreviousPage()"
      />

      <ui-well
        v-if="!features.length"
        class="page-flags__nil"
      >
        No feature flag currently available
      </ui-well>

      <div v-if="features.length" class="page-flags__all-flags">
        <ui-button-link
          :variant="UI.VARIANT.PRIMARY"
          class="font-size-130 text-align-left"
          bulky
          data-cy="toggle_all"
          underline="hover"
          @click="toggleAllFlags"
        >
          <ui-icon class="font-size-135 mr-0.5" name="done_all" />
          Select all flags
        </ui-button-link>
      </div>

      <transition-group
        leave-active-class="animate__animated animate__bounceOutRight ease-in-out a3"
      >

        <div
          v-for="(feature) in features"
          :key="feature"
        >

          <div
            v-if="!isDeprecated(feature)"
            class="page-flags__item"
          >
            <div
              :class="[
                'page-flags__item__title',
                {
                  'color-primary': isSelected(feature)
                }
              ]"
            >
              {{ print(feature) }}
            </div>

            <ui-form-checkbox
              class="page-flags__item__switch"
              :model-value="isSelected(feature)"
              :size="UI.SIZE.LARGE"
              switch
              @update:model-value="() => toggleFeature(feature)"
            />
          </div>

          <div
            v-if="isDeprecated(feature)"
            :class="[
              'page-flags__item',
              'page-flags__item--deprecated'
            ]"
          >
            <ui-wad class="d-flex align-items-center gap-2">
              <div class="page-flags__item__title">
                {{ print(feature) }}
              </div>

              <ui-badge-light
                class="page-flags__item__deprecated"
                :variant="UI.VARIANT.DARK"
              >
                deprecated
              </ui-badge-light>
            </ui-wad>

              <ui-button-icon
                class="page-flags__item__delete"
                :variant="UI.VARIANT.DARK"
                name="delete"
                focus-ring="active"
                focus-ring-variant="dark"
                @click="() => toggleFeature(feature)"
              />

          </div>
        </div>

      </transition-group>

    </div>

  </div>
</template>

<style lang="sass" scoped>

.page-flags
  @extend %fit-waterline

  background: $white

  &__background
    @include inset
    position: absolute
    pointer-events: none
    background-repeat: repeat
    background-image: image('patterns/gravel.png')
    filter: opacity(40%)
    mix-blend-mode: multiply

  &__content
    max-width: map-get($breakpoints, 'tablet')
    width: 80%
    margin: 5vh auto
    padding: space-x(2) space-x(4)

  &__previous
    position: absolute
    left: space-x(2)
    top: space-x(2)
    font-size: 24px
    color: $gray-dark

    &.ui-button-icon:focus::before,
    &.ui-button-icon:active::before,
    &.ui-button-icon:hover::before
      background-color: $white

  &__nil
    margin-bottom: $card-spacer-y
    text-align: center
    font-weight: $font-weight-light
    background-color: mix-alpha($black, 5%)

  &__all-flags
    padding: ($card-spacer-y) ($card-spacer-y)
    background-color: mix-alpha($primary, 5%)
    margin-bottom: $card-spacer-y

  &__item
    position: relative
    padding: ($card-spacer-y) ($card-spacer-y * 2)
    border-radius: $card-border-radius
    background-color: mix-alpha($white, 60%)
    display: flex
    justify-content: space-between
    align-items: center
    margin-bottom: $card-spacer-y
    border: 3px solid mix-alpha($black, .5%)

    &__title
      font-size: 20px
      font-family: $font-family-monospace

    &__deprecated
      font-size: 65%
      opacity: 0.4

    &--deprecated &

      &__title
        text-decoration: line-through
        filter: opacity(0.4)

      &__delete
        // empirical realignement with &__switch
        $offset-x: -16px

        transform: translateX($offset-x)

</style>

<script lang="ts">
import { mapState } from 'pinia'
import { useLinkHandler } from '~/composables/core/link-handler'
import { append, concat, equals, includes, not, pipe, reject, replace, toLower, uniq, values, __, pipe_, writable } from '~/utils/lib/core/prelude'
import { FEATURES as AVAILABLE_FEATURES, type FEATURES, useStore_layout_flags } from '~/stores/layout/flags'

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      layout: 'blank',
      middleware: [
        'session-control-gate',
      ]
    })
    useHead({
      title: `mon-ghs://flags`,
    })

    const store_flags = useStore_layout_flags()

    return {
      ...useLinkHandler(),
      store_flags,
    }
  },
  computed: {
    ...mapState(useStore_layout_flags, [
      'config',
    ]),
    // Dubbing process:
    //  1. take current available features
    //  2. merging user configuration
    //      - if the selected feature is now deprecated, it will be displayed as
    //        as such
    features() :FEATURES[] {
      return pipe_(
        concat(
          values(AVAILABLE_FEATURES),
          writable.iterable(this.config)
        ),
        uniq,
      )
    }
  },
  methods: {
    print: pipe(
      toLower,
      replace(/_/g, ' '),
    ),
    isSelected(feature :FEATURES) {
      return includes(feature, this.config)
    },
    isDeprecated(feature :FEATURES) {
      return pipe_(
        AVAILABLE_FEATURES,
        values,
        includes(feature),
        not
      )
    },
    toggleFeature(feature :FEATURES) {
      const upd = this.isSelected(feature) ?
        reject(equals(feature), this.config) :
        append(feature, this.config)

      this.store_flags.setConfig(upd)
    },
    toggleAllFlags() {
      this.store_flags.setConfig(values(AVAILABLE_FEATURES))
    }
  }
})
</script>
